import { useMutation } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import type {
  PayrollAggregateFeeQuoteDto,
  PayrollFeeQuoteRequestDto,
  PayrollFeeQuoteResponseDto,
} from "@/features/payroll/api/dto";

/**
 * Corps Postman `FeeQuoteRequestDto` : tous les champs du contrat, y
 * compris `transactionReference` (requis à la désérialisation). UUID
 * généré si l'appelant n'en fournit pas — le devis précède l'initiate,
 * donc pas encore de `FPT-…` wallet.
 */
const toFeeQuoteBody = (
  request: PayrollFeeQuoteRequestDto
): PayrollFeeQuoteRequestDto => ({
  productCode: request.productCode,
  providerId: request.providerId ?? null,
  customerSegment: request.customerSegment,
  feePurpose: request.feePurpose,
  amountMinor: request.amountMinor,
  currency: request.currency,
  transactionReference:
    request.transactionReference.trim() || crypto.randomUUID(),
  beneficiaryPartyId: request.beneficiaryPartyId ?? null,
  promoCode: request.promoCode ?? null,
  partyId: request.partyId ?? null,
});

/**
 * Devis de frais AGRÉGÉ d'un lot de paie — un appel `POST /fee-quotes`
 * (fee-engine, §6.5) par bénéficiaire puis somme des montants (le service
 * n'expose pas de devis de lot). Alimente la section frais de la
 * ShareablePreview avant confirmation (règle projet, cahier §9.5).
 *
 * Idempotency-Key OBLIGATOIRE (§4.7) : chaque ligne a un corps différent =
 * une tentative métier distincte → une clé FRAÎCHE par appel.
 *
 * Réplique l'appel du hook `useFeeQuote` de fee-engine via le registre
 * `@/core/api/endpoints` (règle : pas d'import croisé entre features).
 */
export const usePayrollFeeQuote = () =>
  useMutation({
    mutationKey: ["payroll", "fee-quotes", "aggregate"],
    mutationFn: async (
      requests: PayrollFeeQuoteRequestDto[]
    ): Promise<PayrollAggregateFeeQuoteDto> => {
      const quotes: PayrollFeeQuoteResponseDto[] = [];
      for (const reqBody of requests) {
        const quote = await mutateIdempotent<PayrollFeeQuoteResponseDto>({
          scopeId: "payroll.fee-quotes",
          url: endpoints.feeEngine.feeQuotes.create,
          body: toFeeQuoteBody(reqBody),
        });
        quotes.push(quote);
      }
      return {
        grossFeeAmountMinor: quotes.reduce(
          (sum, q) => sum + q.grossFeeAmountMinor,
          0
        ),
        discountAmountMinor: quotes.reduce(
          (sum, q) => sum + q.discountAmountMinor,
          0
        ),
        netFeeAmountMinor: quotes.reduce((sum, q) => sum + q.netFeeAmountMinor, 0),
        currency: requests[0]?.currency ?? "GNF",
        noActiveRule: quotes.some((q) => q.noActiveRule),
      };
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `usePayrollFeeQuote failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
